import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ALL_FORM_OFFICES,
  SIDEBAR_SITE_OPTIONS,
  SIDEBAR_SITE_ALL_LABEL,
  getDefaultSidebarSiteUrl,
} from "@/src/constants/accessControl";
import type { AdmissionOfficeCountry } from "@/src/utils/admissionOffice/resolveAdmissionOfficeRouteFromTask";
import type { FormOfficeApi } from "@/src/utils/tasks/normalizeFormOfficeForApi";
import { resolveCountryAccessFromRole } from "@/src/utils/accessControl/resolveCountryAccessFromRole";

export const SELECTED_SITE_URL_STORAGE_KEY = "selectedSiteUrl";

/**
 * TEMPORARY WITH All: empty / "All" → "" (no site filter).
 * RESTORE WITHOUT All: empty / invalid → first SIDEBAR_SITE_OPTIONS by index (never empty).
 */
const resolveSelectedSiteUrl = (value?: string | null): string => {
  const next = String(value ?? "").trim();

  // --- TEMPORARY WITH All ---
  if (!next || next === SIDEBAR_SITE_ALL_LABEL) {
    return "";
  }
  if ((SIDEBAR_SITE_OPTIONS as readonly string[]).includes(next)) {
    return next;
  }
  return getDefaultSidebarSiteUrl();
  // --- end TEMPORARY WITH All ---

  // --- RESTORE WITHOUT All (production) ---
  // if (next && (SIDEBAR_SITE_OPTIONS as readonly string[]).includes(next)) {
  //   return next;
  // }
  // return getDefaultSidebarSiteUrl();
  // --- end RESTORE WITHOUT All ---
};

const readSelectedSiteUrlFromStorage = (): string => {
  const raw = localStorage.getItem(SELECTED_SITE_URL_STORAGE_KEY);
  // Missing key → default All; explicit "" also means All.
  if (raw === null) return getDefaultSidebarSiteUrl();
  return resolveSelectedSiteUrl(raw);
};

const writeSelectedSiteUrlToStorage = (value: string) => {
  localStorage.setItem(SELECTED_SITE_URL_STORAGE_KEY, value);
};

export interface AccessControlState {
  /** Raw role from auth (`admin`, `ukraine-documents-curator`, …). */
  userRole: string;

  /**
   * Point 1 — site filter (sidebar-office).
   * TEMPORARY WITH All: empty string = All (no site filter).
   * RESTORE WITHOUT All: always a concrete URL from SIDEBAR_SITE_OPTIONS (never empty).
   */
  selectedSiteUrl: string;

  /**
   * Point 2 — reserved for future fine-grained role permissions.
   * Intentionally empty for now.
   */
  rolePermissionsReady: boolean;

  /** Point 3 — country access derived from role. */
  hasFullCountryAccess: boolean;
  allowedOffices: FormOfficeApi[];
  allowedAdmissionCountries: AdmissionOfficeCountry[];
}

const initialFromStorage = (): AccessControlState => {
  const role =
    localStorage.getItem("userRole") ||
    (() => {
      try {
        const raw = localStorage.getItem("userAuthInfo");
        if (!raw) return "";
        const parsed = JSON.parse(raw) as { role?: string };
        return parsed.role ?? "";
      } catch {
        return "";
      }
    })();

  const country = resolveCountryAccessFromRole(role);
  // Persist sidebar-office across reloads until localStorage is cleared.
  const selectedSiteUrl = readSelectedSiteUrlFromStorage();
  writeSelectedSiteUrlToStorage(selectedSiteUrl);

  return {
    userRole: country.userRole,
    selectedSiteUrl,
    rolePermissionsReady: false,
    hasFullCountryAccess: country.hasFullCountryAccess,
    allowedOffices: country.allowedOffices.length ? country.allowedOffices : ALL_FORM_OFFICES,
    allowedAdmissionCountries: country.allowedAdmissionCountries,
  };
};

const initialState: AccessControlState = initialFromStorage();

const accessControlSlice = createSlice({
  name: "accessControl",
  initialState,
  reducers: {
    /** Re-read role from storage (page reload / after login writes). */
    hydrateAccessFromStorage(state) {
      const next = initialFromStorage();
      state.userRole = next.userRole;
      state.selectedSiteUrl = next.selectedSiteUrl;
      state.hasFullCountryAccess = next.hasFullCountryAccess;
      state.allowedOffices = next.allowedOffices;
      state.allowedAdmissionCountries = next.allowedAdmissionCountries;
    },

    /** Apply access from a fresh auth payload (login). */
    setAccessFromUser(
      state,
      action: PayloadAction<{ role?: string | null; selectedSiteUrl?: string }>,
    ) {
      const country = resolveCountryAccessFromRole(action.payload.role);
      state.userRole = country.userRole;
      state.hasFullCountryAccess = country.hasFullCountryAccess;
      state.allowedOffices = country.allowedOffices;
      state.allowedAdmissionCountries = country.allowedAdmissionCountries;

      if (action.payload.selectedSiteUrl !== undefined) {
        const next = resolveSelectedSiteUrl(action.payload.selectedSiteUrl);
        state.selectedSiteUrl = next;
        writeSelectedSiteUrlToStorage(next);
      }
    },

    /** Point 1 — sidebar site selector. Persists value (TEMPORARY: All="" allowed). */
    setSelectedSiteUrl(state, action: PayloadAction<string>) {
      const next = resolveSelectedSiteUrl(action.payload);
      state.selectedSiteUrl = next;
      writeSelectedSiteUrlToStorage(next);
    },

    clearAccessControl(state) {
      const defaultSiteUrl = getDefaultSidebarSiteUrl();
      state.userRole = "";
      state.selectedSiteUrl = defaultSiteUrl;
      state.hasFullCountryAccess = true;
      state.allowedOffices = ALL_FORM_OFFICES;
      state.allowedAdmissionCountries = ALL_FORM_OFFICES.map((office) =>
        office === "MALTA" ? "malta" : office === "UKRAINE" ? "ukraine" : "poland",
      );
      writeSelectedSiteUrlToStorage(defaultSiteUrl);
    },
  },
});

export const {
  hydrateAccessFromStorage,
  setAccessFromUser,
  setSelectedSiteUrl,
  clearAccessControl,
} = accessControlSlice.actions;

export default accessControlSlice.reducer;
